import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Inner-tab selection for a workflow step, synced to `?section=`.
 *
 * Steps 7 and 8 hold several tables behind an inner tab nav. Without this the
 * tab was local state only, so nothing outside the step could address it — a
 * deep link could reach the step but always landed on its first tab. That is
 * what sent "Go to step" for V&V Recommendations to the Controls tab (and,
 * before the step number existed, to Item Definition entirely).
 *
 * Mirrors the `?section=` handling Step 1 already uses, so all steps behave the
 * same way on refresh and back/forward.
 */
export function useStepSection(defaultKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(
    () => searchParams.get('section') || defaultKey,
  );

  // Don't write the param on the initial render — only once the user (or a
  // later deep link) actually changes tabs. Otherwise a step that mounts with
  // no section would immediately stamp its default into the URL.
  const seededRef = useRef(false);
  useEffect(() => {
    seededRef.current = true;
  }, []);

  useEffect(() => {
    if (!seededRef.current) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('section') === activeTab) return prev;
        next.set('section', activeTab);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParams]);

  return [activeTab, setActiveTab] as const;
}

/**
 * Snap a wanted key onto one that exists in the current tab set.
 *
 * Steps 7 and 8 rename their tabs in hierarchical mode (`goals` becomes
 * `goals:current` / `goals:child`). A link therefore addresses the *family*
 * (`goals`, `claims`, `controls`, `requirements`, `vv`) and this resolves it to
 * whichever concrete tab exists — otherwise `claims` in hierarchical mode would
 * fall through to the first tab and silently show Goals.
 */
export function resolveSectionKey(
  tabs: ReadonlyArray<{ key: string }>,
  want: string,
): string {
  if (tabs.some((t) => t.key === want)) return want;
  const family = tabs.find((t) => t.key.startsWith(`${want}:`));
  return family?.key ?? tabs[0]?.key ?? '';
}
